import { useEffect, useRef, useState } from 'react';
import { Download, FileCheck2, ShieldX } from 'lucide-react';
import { chamarApi, ErroApi } from '../../../nucleo/auth/cliente-api';
import { PageHeader } from '../../../ui/PageHeader';
import { EmptyState } from '../../../ui/EmptyState';
import { Alert, AlertDescription } from '../../../ui/base/alert';
import { Button } from '../../../ui/base/button';
import { Input } from '../../../ui/base/input';
import { Label } from '../../../ui/base/label';
const NS = '/bff/v1/j12-s03-exportar-documento';
type R = {
  id: string;
  instante_corte: string;
  total: string;
  hash_reproduzivel: string;
  nivel_detalhe: string;
};
type D = {
  id: string;
  resultado_id: string;
  formato: string;
  codigo_verificacao: string;
  nome_arquivo: string;
  tamanho_bytes: number;
  sha256: string;
  autor: string;
  finalidade: string | null;
  instante_corte: string;
  estado: string;
  motivo_revogacao: string | null;
  downloads: number;
  created_at: string;
};
type S = {
  fase: string;
  titulo: string;
  descricao: string;
  resultados: R[];
  documentos: D[];
  documentoSelecionado: D | null;
  download: null | { nome: string; mimeType: string; conteudoBase64: string; sha256: string };
  alertas: Array<{ mensagem: string }>;
  permissoes: { exportar: boolean; baixar: boolean; revogar: boolean };
  resultadoOperacao?: { mensagem: string } | null;
};
type E = { state: S };
export function ExportarDocumentoPage() {
  const [e, se] = useState<S | null>(null),
    [erro, setErro] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [resultado, setResultado] = useState(''),
    [formato, setFormato] = useState<'JSON' | 'CSV'>('JSON'),
    [finalidade, setFinalidade] = useState('Prestação de contas e auditoria institucional'),
    [motivo, setMotivo] = useState(''),
    chave = useRef(crypto.randomUUID());
  async function v(nome: string, corpo: unknown = {}) {
    setBusy(true);
    setErro(null);
    try {
      const x = (await chamarApi<E>(`${NS}/verbs/${nome}`, { metodo: 'POST', corpo })).state;
      se(x);
      if (x.download) baixar(x.download);
    } catch (x) {
      setErro(x instanceof ErroApi ? x.aviso : 'Não foi possível concluir.');
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void chamarApi<E>(`${NS}/state`)
      .then((x) => {
        se(x.state);
        setResultado(x.state.resultados[0]?.id ?? '');
      })
      .catch(() => setErro('Não foi possível carregar os documentos.'));
  }, []);
  if (!e)
    return (
      <EmptyState
        variant={erro ? 'error' : 'default'}
        title={erro ? 'Não foi possível carregar' : 'Carregando documentos…'}
        description={erro ?? 'Aguarde.'}
      />
    );
  return (
    <section className="min-w-0 max-w-[calc(100vw-2rem)] overflow-hidden">
      <PageHeader title={e.titulo} description={e.descricao} />
      {[...e.alertas.map((a) => a.mensagem), ...(erro ? [erro] : [])].map((m, i) => (
        <Alert className="mb-3" key={i}>
          <AlertDescription>{m}</AlertDescription>
        </Alert>
      ))}
      <article className="mb-5 grid gap-3 rounded-md border p-4">
        <h2 className="font-semibold">Emitir novo documento</h2>
        <Label htmlFor="resultado">Resultado completo</Label>
        <select
          id="resultado"
          className="h-10 rounded-md border bg-background px-3"
          value={resultado}
          onChange={(x) => setResultado(x.target.value)}
        >
          {e.resultados.map((r) => (
            <option key={r.id} value={r.id}>
              {r.total} · {r.nivel_detalhe} · {r.id}
            </option>
          ))}
        </select>
        <Label htmlFor="formato">Formato oficial</Label>
        <select
          id="formato"
          className="h-10 rounded-md border bg-background px-3"
          value={formato}
          onChange={(x) => setFormato(x.target.value as 'JSON' | 'CSV')}
        >
          <option>JSON</option>
          <option>CSV</option>
        </select>
        <Label htmlFor="finalidade">Finalidade</Label>
        <Input id="finalidade" value={finalidade} onChange={(x) => setFinalidade(x.target.value)} />
        <Button
          disabled={busy || !resultado}
          onClick={() =>
            void v('exportar_documento', {
              resultadoId: resultado,
              formato,
              finalidade,
              chaveIdempotencia: chave.current,
            })
          }
        >
          <FileCheck2 className="mr-2 h-4 w-4" />
          Emitir documento oficial
        </Button>
      </article>
      {e.documentos.length === 0 ? (
        <EmptyState
          title="Nenhum documento emitido"
          description="Emita um resultado completo para iniciar a verificação."
        />
      ) : (
        <div className="grid gap-3">
          {e.documentos.map((d) => (
            <article key={d.id} className="rounded-md border p-4">
              <h2 className="font-semibold">
                {d.nome_arquivo} · {d.estado}
              </h2>
              <p className="mt-1 text-sm">
                Código <strong>{d.codigo_verificacao}</strong> · {d.tamanho_bytes} bytes ·{' '}
                {d.downloads} download(s)
              </p>
              <p className="mt-2 break-all text-xs text-muted-foreground">
                SHA-256 {d.sha256}
                <br />
                Autor {d.autor} · corte {new Date(d.instante_corte).toLocaleString('pt-BR')}
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => void v('baixar_documento', { documentoId: d.id })}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Baixar e registrar
                </Button>
                {d.estado === 'VIGENTE' && (
                  <>
                    <Input
                      aria-label="Motivo da revogação"
                      placeholder="Motivo da revogação"
                      value={motivo}
                      onChange={(x) => setMotivo(x.target.value)}
                    />
                    <Button
                      variant="destructive"
                      disabled={busy || motivo.trim().length < 10}
                      onClick={() => void v('revogar_documento', { documentoId: d.id, motivo })}
                    >
                      <ShieldX className="mr-2 h-4 w-4" />
                      Revogar
                    </Button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
function baixar(d: { nome: string; mimeType: string; conteudoBase64: string }) {
  const b = atob(d.conteudoBase64.replace(/\s/g, '')),
    a = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) a[i] = b.charCodeAt(i);
  const u = URL.createObjectURL(new Blob([a], { type: d.mimeType })),
    l = document.createElement('a');
  l.href = u;
  l.download = d.nome;
  l.click();
  URL.revokeObjectURL(u);
}
